import { equalsUrl, URLX } from '../url';
import { type HistoryEvent, HistoryEventType, historyEvent, updateCurrentUrlWithoutEvents } from '../client/history';
import { scrollPosition } from '../scrolling';
import {
  loadPage,
  currentPageLoadFailure,
  isPage,
  isPageError,
  type PageBase,
  type PageError,
  currentPage,
  currentPageLoadSuccessful,
} from './state';
import { isAsyncObservable, createAsyncObservable, type AsyncObservable } from '../events';
import { createSyncObservable } from '@avensia/observable';
import { setUpdateIsUsignHydration } from '../update-is-using-hydration';

export const [pageRenderingDoneSignal] = createSyncObservable<HistoryEvent>({} as HistoryEvent);

let cancelCurrentOperation: (() => unknown) | undefined = undefined;

export function initializeHistorySync() {
  currentPage.on(page => {
    Array.from(document.querySelectorAll('meta[data-excite], link[data-excite]')).forEach(node => {
      if (node.parentElement) {
        node.parentElement.removeChild(node);
      }
    });
    if (isPage(page) && page.metaInfo) {
      const meta = page.metaInfo;
      document.title = meta.title ?? '';

      Object.keys(meta.elements || {}).forEach(name => {
        const metaElement = document.createElement('meta');
        metaElement.setAttribute('data-excite', 'true');
        metaElement.setAttribute(meta.elements[name].type, name);
        metaElement.setAttribute('content', meta.elements[name].value);
        if (document.head) {
          document.head.appendChild(metaElement);
        }
      });

      if (meta.canonicalUrl) {
        const linkElement = document.createElement('link');
        linkElement.setAttribute('data-excite', 'true');
        linkElement.setAttribute('rel', 'canonical');
        linkElement.setAttribute('href', meta.canonicalUrl);
        if (document.head) {
          document.head.appendChild(linkElement);
        }
      }
      if (meta.openGraphUrl) {
        const linkElement = document.createElement('link');
        linkElement.setAttribute('data-excite', 'true');
        linkElement.setAttribute('rel', 'og:url');
        linkElement.setAttribute('href', meta.openGraphUrl);
        if (document.head) {
          document.head.appendChild(linkElement);
        }
      }
    }
  });

  historyEvent.on(async (event, previousEvent) => {
    if (event.options.skipLoad) {
      return;
    }

    if (cancelCurrentOperation) {
      cancelCurrentOperation();
      cancelCurrentOperation = undefined;
    }
    if (previousEvent || event.previousState) {
      // If we're on the same url (disregarding hash) and the new event or the previous event
      // has UI state it means that the history operation should not trigger
      // a component load.
      if (
        equalsUrl(event.url, previousEvent?.url ?? new URLX(event.previousState.url)) &&
        event.options.page === previousEvent?.options.page &&
        event.event !== HistoryEventType.Pop
      ) {
        return;
      }
    }

    if (event.state.clientParams || event.previousState.clientParams) {
      const previousUrl = new URL(event.previousState.url);
      const previousServerParams = previousUrl.searchParams;
      const eventServerParams = new URLSearchParams(event.url.search);
      if (event.state.clientParams) {
        for (const key in event.state.clientParams) {
          previousServerParams.delete(key);
          eventServerParams.delete(key);
        }
      }
      if (event.previousState.clientParams) {
        for (const key in event.previousState.clientParams) {
          previousServerParams.delete(key);
          eventServerParams.delete(key);
        }
      }

      // If the only thing in the url that differes between this event and the previous
      // event is the client params we skip doing anything as it's up to the component that
      // triggered the client param to take action.
      if (
        event.url.pathname === previousUrl.pathname &&
        previousServerParams.toString() === eventServerParams.toString()
      ) {
        return;
      }
    }

    setUpdateIsUsignHydration(false);

    let observable: AsyncObservable<PageBase | PageError>;
    if (event.options.page) {
      observable = isAsyncObservable<PageBase | PageError>(event.options.page)
        ? event.options.page
        : createAsyncObservable(event.options.page)[0];
    } else {
      observable = loadPage(event.url, {
        componentName: event.options.componentName,
        linkIdentifier: event.options.linkIdentifier,
        merge: event.options.merge,
        extendPartialCache: event.options.extendPartialCache,
        keepComponents: event.options.keepComponents,
        shouldFetchData(cachedData) {
          if (typeof event.options.shouldFetchData === 'boolean') {
            return event.options.shouldFetchData;
          }
          if (event.options.shouldFetchData) {
            return event.options.shouldFetchData(cachedData);
          }
          return cachedData.cache !== 'full' || !(cachedData.cache === 'full' && event.event === HistoryEventType.Pop);
        },
        shouldUseCache(cachedData) {
          if (typeof event.options.shouldUseCache === 'boolean') {
            return event.options.shouldUseCache;
          }
          if (event.options.shouldUseCache) {
            return event.options.shouldUseCache(cachedData);
          }

          return true;
        },
      });
    }

    let hasSetScrollPosition = false;
    cancelCurrentOperation = observable.on(async value => {
      if (value.url !== event.url.pathAndQuery && (value.httpStatus === 301 || value.httpStatus === 302)) {
        // This means that the server redirected to another page
        // so we update the url in the address bar to reflect that
        updateCurrentUrlWithoutEvents(new URLX(value.url));
      }

      const renderingDone = currentPage.emit(value);
      if (isPage(value)) {
        currentPageLoadSuccessful.emit(value);

        if (currentPageLoadFailure.currentSync() !== undefined) {
          currentPageLoadFailure.emit(undefined);
        }

        if (!hasSetScrollPosition && event.scrollPosition !== undefined) {
          hasSetScrollPosition = true;
          await renderingDone;
          if (!event.options.skipUpdateScrollPosition) {
            scrollPosition(event.scrollPosition);
          }
          pageRenderingDoneSignal.emit(event);
        }
      } else if (isPageError(value)) {
        currentPageLoadFailure.emit(value);
      }
    });

    // This lets us return the last state of the component to the caller.
    // This lets you do:
    // const emitResults = await history.pushState(url);
    // The promise returned by pushState() will resolve once
    // the new page is fully loaded.
    await observable.whenClosed;
    const current = observable.currentSync();
    if (isPage(current)) {
      return current;
    } else {
      throw current;
    }
  });
}
